'use client';

import { useState, type MouseEvent, type ReactNode } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  SourceCodeIcon,
  Globe02Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Text } from '../../../components/styled/text';
import { Tooltip } from '../../../components/tooltip';
import { HttpArtifact } from '../http/HttpArtifact';
import { ReadOnlyCodeEditor } from '../../chat/components/ReadOnlyCodeEditor';
import { filterFileUploadDisplayArgs } from '../../chat/trace-view/components/tool-args-display.util';
import { useTraceViewSettings } from '../../chat/trace-view/TraceViewSettingsContext';
import { type TraceToolCall } from '../../chat/trace-view/types';
import { normalizeToolName } from '../../chat/trace-view/utils/tool-config';
import { shouldShowTraceToolShimmer } from '../../chat/trace-view/utils/trace-text-shimmer';
import {
  buildPrepareFileResultContent,
  toPrettyJson,
} from './file-tool-artifact.utils';

type FileToolArtifactProps = {
  toolCall: TraceToolCall;
  displayName: string;
};
type FileToolSectionId = 'input' | 'result';

function buildInputContent({
  toolName,
  args,
}: {
  toolName: string;
  args: Record<string, unknown>;
}): string {
  return toPrettyJson(filterFileUploadDisplayArgs({ toolName, args }));
}

function FileToolSection({
  sectionId,
  label,
  isExpanded,
  onToggle,
  children,
  showBottomBorder,
}: {
  sectionId: FileToolSectionId;
  label: string;
  isExpanded: boolean;
  onToggle: (sectionId: FileToolSectionId) => void;
  children: ReactNode;
  showBottomBorder?: boolean;
}) {
  return (
    <Box
      bg="transparent"
      borderBottomWidth={showBottomBorder ? '1px' : '0'}
      borderBottomColor="border.neutral.muted"
      width="100%"
    >
      <HStack
        px={2}
        h="32px"
        cursor="pointer"
        onClick={() => onToggle(sectionId)}
        width="100%"
        gap={1.5}
        alignItems="center"
      >
        <Box display="flex" alignItems="center" color="fg.muted">
          {isExpanded ? (
            <ArrowDown01Icon size={10} />
          ) : (
            <ArrowRight01Icon size={10} />
          )}
        </Box>
        <Text fontSize="xs" fontWeight="medium" color="fg.muted">
          {label}
        </Text>
      </HStack>

      {isExpanded && (
        <Box
          borderTopWidth="1px"
          borderColor="border.neutral.muted"
          bg="transparent"
          maxH="400px"
          overflow="auto"
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

function FileToolViewToggle({
  showHttpView,
  onShowInput,
  onShowHttp,
}: {
  showHttpView: boolean;
  onShowInput: () => void;
  onShowHttp: () => void;
}) {
  const t = useTranslations('designSystem.artifacts');
  const handleShowInput = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onShowInput();
  };

  const handleShowHttp = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onShowHttp();
  };

  return (
    <HStack
      gap="0"
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="sm"
      overflow="hidden"
      flexShrink={0}
    >
      <Tooltip title={t('showCode')}>
        <Box
          as="button"
          aria-label={t('showCode')}
          onClick={handleShowInput}
          h="5"
          w="5"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          bg={!showHttpView ? 'bg.muted' : 'transparent'}
          color={!showHttpView ? 'fg.default' : 'fg.muted'}
          _hover={{
            bg: !showHttpView ? 'bg.muted' : 'bg.hover',
          }}
        >
          <SourceCodeIcon size={10} />
        </Box>
      </Tooltip>
      <Tooltip title={t('showHttpRequests')}>
        <Box
          as="button"
          aria-label={t('showHttpRequests')}
          onClick={handleShowHttp}
          h="5"
          w="5"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          bg={showHttpView ? 'bg.muted' : 'transparent'}
          color={showHttpView ? 'fg.default' : 'fg.muted'}
          _hover={{
            bg: showHttpView ? 'bg.muted' : 'bg.hover',
          }}
        >
          <Globe02Icon size={10} />
        </Box>
      </Tooltip>
    </HStack>
  );
}

function FileToolCodeBody({
  inputContent,
  resultContent,
  inputLabel,
  resultLabel,
}: {
  inputContent: string;
  resultContent: string;
  inputLabel: string;
  resultLabel: string;
}) {
  const [expandedSections, setExpandedSections] = useState({
    input: false,
    result: false,
  });
  const hasInput = inputContent.trim().length > 0;
  const hasResult = resultContent.trim().length > 0;
  const toggleSection = (sectionId: FileToolSectionId) => {
    setExpandedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  };

  return (
    <>
      {hasInput && (
        <FileToolSection
          sectionId="input"
          label={inputLabel}
          isExpanded={expandedSections.input}
          onToggle={toggleSection}
          showBottomBorder={hasResult}
        >
          <ReadOnlyCodeEditor
            value={inputContent}
            maxHeight="300px"
            minHeight="44px"
            language="json"
            showLineNumbers
          />
        </FileToolSection>
      )}

      {hasResult && (
        <FileToolSection
          sectionId="result"
          label={resultLabel}
          isExpanded={expandedSections.result}
          onToggle={toggleSection}
        >
          <ReadOnlyCodeEditor
            value={resultContent}
            maxHeight="300px"
            minHeight="44px"
            language={resultContent.trim().startsWith('{') ? 'json' : 'text'}
            showLineNumbers
          />
        </FileToolSection>
      )}
    </>
  );
}

export function FileToolArtifact({
  toolCall,
  displayName,
}: FileToolArtifactProps) {
  const t = useTranslations('designSystem.artifacts');
  const tChat = useTranslations('designSystem.chat');
  const { shouldShowHttpView, setCardHttpView } = useTraceViewSettings();
  const normalizedName = normalizeToolName(toolCall.name);
  const isFileUpload = normalizedName === 'file-upload-client';
  const inputContent = buildInputContent({
    toolName: toolCall.name,
    args: toolCall.args,
  });
  const resultContent = isFileUpload
    ? (toolCall.output ?? toolCall.error ?? '')
    : buildPrepareFileResultContent(toolCall.output ?? toolCall.error);
  const isFinished =
    toolCall.status === 'completed' || toolCall.status === 'error';
  const hasInput = inputContent.trim().length > 0;
  const hasResult = resultContent.trim().length > 0;
  const hasContent = hasInput || hasResult || (isFileUpload && isFinished);
  const showHttpToggle = isFileUpload && isFinished;
  const showHttpView = showHttpToggle && shouldShowHttpView(toolCall.id);
  const showShimmer = shouldShowTraceToolShimmer({
    hasResult: hasContent,
    status: toolCall.status,
  });
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;

  if (!hasContent) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        <Text
          fontSize="xs"
          color="fg.muted"
          animationStyle={showShimmer ? 'shimmer' : undefined}
          style={shimmerStyle}
        >
          {displayName}
        </Text>
      </HStack>
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={2}
        h="28px"
        bg="transparent"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <Text fontSize="xs" fontWeight="medium" color="fg.default">
          {displayName}
        </Text>
        {showHttpToggle && (
          <FileToolViewToggle
            showHttpView={showHttpView}
            onShowInput={() => setCardHttpView(toolCall.id, false)}
            onShowHttp={() => setCardHttpView(toolCall.id, true)}
          />
        )}
      </HStack>

      {showHttpView ? (
        <HttpArtifact
          toolCallId={toolCall.id}
          toolType="HTTP"
          showSummaryHeader
          embedded
        />
      ) : (
        <FileToolCodeBody
          inputContent={inputContent}
          resultContent={resultContent}
          inputLabel={tChat('input')}
          resultLabel={t('result')}
        />
      )}
    </Box>
  );
}
